/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1744761600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Apr 16 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "pinterest-ads",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Pinterest Ads",
          "depth": 0,
          "id": "connect-to-pinterest-ads"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing conversion events",
          "depth": 1,
          "id": "syncing-conversion-events"
        }, {
          "value": "Syncing customer list audiences",
          "depth": 1,
          "id": "syncing-customer-list-audiences"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {EventSyncWarning, MatchBoosterDestination, MatchRatesExplanation, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!MatchRatesExplanation) _missingMdxReference("MatchRatesExplanation", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Manage your conversion tracking by syncing conversion events and promote your pins to a specific Pinterest audience by syncing customer lists from your data warehouse. Deliver more relevant ads, boost marketing ROI, and retarget your most loyal customers."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Object Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Conversion Events"
              })
            }), _jsx(_components.td, {
              children: "Sync conversion events from any source to Pinterest Ads"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customer List Audiences"
              })
            }), _jsx(_components.td, {
              children: "Sync your PII data from any source to Pinterest Ads customer list audiences"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-pinterest-ads",
        children: "Connect to Pinterest Ads"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Pinterest Ads"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Pinterest Ads by selecting ", _jsx(_components.strong, {
          children: "Log in to Pinterest Ads"
        }), " and logging into your Pinterest Ads account."]
      }), "\n", _jsxs(_components.p, {
        children: ["Once successful, you will be redirected back to Hightouch to enter your ", _jsx(_components.strong, {
          children: "advertiser account ID"
        }), " and ", _jsx(_components.strong, {
          children: "conversion access token"
        }), "."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Advertiser account ID"
            }), ": You can locate your advertiser account ID within your Pinterest business account by navigating to\nthe dropdown button on the top right of the page, then ", _jsx(_components.strong, {
              children: "Business Access"
            }), " > ", _jsx(_components.strong, {
              children: "Ad Accounts"
            }), ". You can find your ID under your the ", _jsx(_components.strong, {
              children: "Asset name"
            }), "."]
          }), "\n", _jsx(_components.p, {
            children: _jsx(_components.img, {
              src: "destinations/destination-pinterest-setup.png",
              alt: "Locate advertiser account ID"
            })
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Conversion access token"
            }), ": If you want to send conversion events to Pinterest Ads, you also need to input a conversion access token. You can create a conversion access token within your Pinterest business account by navigating to the ", _jsx(_components.strong, {
              children: "Ads"
            }), " dropdown then ", _jsx(_components.strong, {
              children: "Conversions"
            }), " > ", _jsx(_components.strong, {
              children: "Conversion access token"
            }), ". Click ", _jsx(_components.strong, {
              children: "Generate new token"
            }), " and enter the token in Hightouch."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-pinterest-conversion-access-token.png",
          alt: "Create conversion access token"
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Pinterest Ads destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Pinterest Ads destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-conversion-events",
        children: "Syncing conversion events"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing conversion events to Pinterest Ads."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match events from your source to Pinterest Ads using the Event ID."
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["You can use ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), " to sync data to the event properties of a Pinterest Ads conversion event. Refer to the ", _jsx(_components.a, {
          href: "https://developers.pinterest.com/docs/api/v5/#operation/events/create",
          children: "Pinterest Ads developer docs"
        }), " for descriptions of each property.\nEnsure your model columns' data types match the data types of the fields you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["For fields that expect an array , Hightouch automatically converts your row values to arrays if they aren't already. For example, you can map ", _jsx(_components.a, {
          href: "/models/creating-models#data-types-and-casting",
          children: "string model columns"
        }), " to array type Pinterest Ads fields."]
      }), "\n", _jsxs(_components.p, {
        children: ["The Pinterest Ads API requires the ", _jsx(_components.strong, {
          children: "Action Source"
        }), " field, so you must map it to complete your configuration.\n", _jsx(_components.strong, {
          children: "Action Source"
        }), " is an enumerated field which only accepts the following ", _jsx(_components.code, {
          children: "string"
        }), " values:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "\"app_android\""
        }), "\n", _jsx(_components.li, {
          children: "\"app_ios\""
        }), "\n", _jsx(_components.li, {
          children: "\"web\""
        }), "\n", _jsx(_components.li, {
          children: "\"offline\""
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii",
        children: "Handling PII"
      }), "\n", _jsxs(_components.p, {
        children: ["Pinterest Ads requires some identity information to be normalized and hashed. We support hashing for all fields that require values to be SHA256 hashed. Disabling PII hashing requires you to do this yourself.\nIf you're handling hashing yourself, make sure that email addresses are in lowercase before hashing, as explained in ", _jsx(_components.a, {
          href: "https://developers.pinterest.com/docs/api/v5/#operation/events/create",
          children: "Pinterest's documentation"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "cleaning-phone-numbers",
        children: "Cleaning phone numbers"
      }), "\n", _jsx(_components.p, {
        children: "You can select whether Hightouch should clean your phone numbers. By default, Hightouch doesn't clean phone numbers."
      }), "\n", _jsx(_components.p, {
        children: "Pinterest Ads requires users' phone numbers to be normalized and hashed. The phone number should only include digits with country code, area code, and number without any symbols, letters, spaces, and leading zeros."
      }), "\n", _jsxs(_components.p, {
        children: ["If you enable cleaning phone numbers, Hightouch tries to convert the phone number into E.164 format and remove the leading plus (", _jsx(_components.code, {
          children: "+"
        }), ") symbol."]
      }), "\n", _jsx(_components.h4, {
        id: "using-the-test-events-endpoint",
        children: "Using the test events endpoint"
      }), "\n", _jsxs(_components.p, {
        children: ["Pinterest Ads provides a ", _jsx(_components.a, {
          href: "https://help.pinterest.com/en/business/article/validating-your-set-up-with-event-testing",
          children: "test events endpoint"
        }), " to validate your set up for conversion events. This endpoint does not affect\nproduction conversion data, and returns warnings and recommendations based on your set up. Syncs can be run using this endpoint and results can be seen by clicking on ", _jsx(_components.strong, {
          children: "Test Events"
        }), " under the the ", _jsx(_components.strong, {
          children: "Conversions Tab"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-customer-list-audiences",
        children: "Syncing customer list audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "customer list audience"
        }), " is a ", _jsx(_components.a, {
          href: "https://help.pinterest.com/en/business/article/audience-targeting",
          children: "customer list that is added to an audience"
        }), ".\nDepending on your model query results, Hightouch adds new user records into the Pinterest Ads customer list audience or removes them."]
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "To identify which users to add or update in an audience, select model columns and the corresponding Pinterest Ads user fields. You can match on any of the following Pinterest Ads user fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "IDFA"
        }), "\n", _jsx(_components.li, {
          children: "MAID"
        }), "\n", _jsx(_components.li, {
          children: "LR_ID"
        }), "\n", _jsx(_components.li, {
          children: "DLX_ID"
        }), "\n", _jsx(_components.li, {
          children: "Hashed Pinner ID"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Emails must be lowercase and can be plain text or hashed using SHA1, SHA256,\nor MD5. MAIDs and IDFAs must be hashed with SHA1, SHA256, or MD5."
        })
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Pinterest Ads"
      }), "\n", _jsx(_components.h4, {
        id: "new-audience-creation",
        children: "New audience creation"
      }), "\n", _jsxs(_components.p, {
        children: ["In your sync configuration, you can tell Hightouch to create a new customer list audience for you. You can optionally assign a custom name to your audience. If no name is provided, Hightouch defaults to using your model name.\nTo help differentiate the name of your customer list and audience, we save the customer list name as ", _jsx(_components.code, {
          children: "customer list-{customer list name}"
        }), " and audience name as ", _jsx(_components.code, {
          children: "audience-{audience name}"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "The following example screenshot would create a new customer audience list called \"Users 1.\""
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-pinterest-create-new-list.png",
          alt: "Creating a new customer list audience"
        })
      }), "\n", _jsx(_components.p, {
        children: "After creation, the customer list name will be \"customer list-Users 1,\" and the audience name will be \"audience-Users 1.\""
      }), "\n", _jsx(_components.h4, {
        id: "using-an-existing-audience",
        children: "Using an existing audience"
      }), "\n", _jsx(_components.p, {
        children: "Instead of creating a new data customer list audience, Hightouch can also sync users to existing audiences that are already populated in your Pinterest Ads account.\nThe Hightouch UI shows the customer list and audience as a comma-separated string."
      }), "\n", _jsx(_components.p, {
        children: "In the example screenshot below, the customer list audience shows as \"customer list-Users 1, audience-Users 1.\""
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-pinterest-use-existing-list.png",
          alt: "Using an existing customer list audience"
        })
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your model to the following identifiers in Pinterest:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "IDFA"
        }), "\n", _jsx(_components.li, {
          children: "MAID"
        }), "\n", _jsx(_components.li, {
          children: "LR_ID"
        }), "\n", _jsx(_components.li, {
          children: "DLX_ID"
        }), "\n", _jsx(_components.li, {
          children: "Hashed Pinner ID"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), " can help to provide additional identifiers and increase your match rates."]
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii-1",
        children: "Handling PII"
      }), "\n", _jsxs(_components.p, {
        children: ["Pinterest Ads requires some identity information to be normalized and hashed. Hightouch supports hashing for all user types except for ", _jsx(_components.strong, {
          children: "Email"
        }), ", since Pinterest Ads doesn't require hashing this field."]
      }), "\n", _jsx(_components.p, {
        children: "Enable PII hashing if you wish for Hightouch to normalize and hash PII fields using a SHA256 hash. Disabling PII hashing requires to do this yourself."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsx(_components.p, {
        children: "Below only applies to the audiences sync type."
      }), "\n", _jsx(MatchRatesExplanation, {
        destination: "Pinterest Ads"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "delayed-syncing",
        children: "Delayed syncing"
      }), "\n", _jsxs(_components.p, {
        children: ["Pinterest takes around 24 hours to fully sync after Hightouch has synced data to their API. You can find more details under ", _jsx(_components.strong, {
          children: "customer list"
        }), " on ", _jsx(_components.a, {
          href: "https://help.pinterest.com/en/business/article/audience-targeting",
          children: "Pinterest's audience targeting help page"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "smaller-audience-size",
        children: "Smaller audience size"
      }), "\n", _jsxs(_components.p, {
        children: ["You may notice that the audience size may be smaller than the total number of records synced to Pinterest Ads. This is because ", _jsx(_components.a, {
          href: "https://developers.pinterest.com/docs/api/v5/#tag/customer_lists",
          children: "Pinterest filters out\nrecords that don't have a Pinterest account"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "400---invalid-request",
        children: "400 - Invalid request"
      }), "\n", _jsxs(_components.p, {
        children: ["You may receive a ", _jsx(_components.code, {
          children: "400 - {\"code\":1,\"message\":\"Invalid request: {} is not valid under any of the given schemas\"}"
        }), " error message because a row being sent to Pinterest contains a ", _jsx(_components.code, {
          children: "null"
        }), " or blank value."]
      }), "\n", _jsxs(_components.p, {
        children: ["Ensure that mapped fields do not contain any", _jsx(_components.code, {
          children: "null"
        }), " or blank values. You can resolve this error by:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Filtering these rows in the model's query"
        }), "\n", _jsxs(_components.li, {
          children: ["Selecting the ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/syncs/mapping-data#dont-sync-null-values",
            children: "Don't sync null values"
          }), " checkbox in of the field mapping configurations."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
